/**
 * FinderMazesPage — canvas-generated printable mazes.
 *
 * Uses depth-first-search maze generation. Client-side only — the
 * canvas can be printed or saved as PNG (data URL). No backend calls.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";

const SIZES = [
  { key: "kid",   label: "Kid",   cols: 12, rows: 12 },
  { key: "family", label: "Family", cols: 20, rows: 20 },
  { key: "hard",  label: "Hard",  cols: 30, rows: 30 },
];

// ── Themes decorate the maze with a start/finish icon and a tint.
// Each icon is a small inline SVG rendered as a data URL so the whole
// page still works offline.
const THEMES = [
  {
    key: "classic",
    label: "Classic",
    icon: "⚪",
    tint: null,
    start: { color: "#10b981", label: "START" },
    end:   { color: "#f59e0b", label: "FINISH" },
    drawStart: (ctx, cx, cy, s) => {
      ctx.fillStyle = "#10b981";
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.28, 0, Math.PI * 2);
      ctx.fill();
    },
    drawEnd: (ctx, cx, cy, s) => {
      ctx.fillStyle = "#f59e0b";
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.28, 0, Math.PI * 2);
      ctx.fill();
    },
  },
  {
    key: "koala",
    label: "Koala",
    icon: "🐨",
    tint: "#e6f4ea",
    start: { color: "#10b981", label: "KOALA" },
    end:   { color: "#8b4513", label: "GUM TREE" },
    drawStart: (ctx, cx, cy, s) => {
      // koala face
      ctx.fillStyle = "#a1a4a8";
      ctx.beginPath(); ctx.arc(cx, cy, s * 0.36, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx - s*0.28, cy - s*0.2, s*0.16, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + s*0.28, cy - s*0.2, s*0.16, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = "#000";
      ctx.beginPath(); ctx.arc(cx - s*0.12, cy - s*0.05, s*0.05, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + s*0.12, cy - s*0.05, s*0.05, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(cx, cy + s*0.12, s*0.14, s*0.09, 0, 0, Math.PI*2); ctx.fill();
    },
    drawEnd: (ctx, cx, cy, s) => {
      ctx.fillStyle = "#8b4513";
      ctx.fillRect(cx - s*0.1, cy - s*0.05, s*0.2, s*0.4);
      ctx.fillStyle = "#4b8f5c";
      ctx.beginPath(); ctx.arc(cx, cy - s*0.15, s*0.35, 0, Math.PI * 2); ctx.fill();
    },
  },
  {
    key: "kangaroo",
    label: "Kangaroo",
    icon: "🦘",
    tint: "#fef3e7",
    start: { color: "#a05a2c", label: "ROO" },
    end:   { color: "#7a3110", label: "OUTBACK" },
    drawStart: (ctx, cx, cy, s) => {
      ctx.fillStyle = "#a05a2c";
      // body
      ctx.beginPath(); ctx.ellipse(cx, cy + s*0.05, s*0.28, s*0.32, 0, 0, Math.PI*2); ctx.fill();
      // head
      ctx.beginPath(); ctx.arc(cx + s*0.2, cy - s*0.22, s*0.18, 0, Math.PI*2); ctx.fill();
      // ear
      ctx.fillRect(cx + s*0.24, cy - s*0.45, s*0.06, s*0.15);
      // tail
      ctx.beginPath(); ctx.moveTo(cx, cy + s*0.15); ctx.lineTo(cx - s*0.35, cy + s*0.35); ctx.lineTo(cx - s*0.35, cy + s*0.45); ctx.closePath(); ctx.fill();
    },
    drawEnd: (ctx, cx, cy, s) => {
      // Uluru silhouette
      ctx.fillStyle = "#a54318";
      ctx.beginPath();
      ctx.moveTo(cx - s*0.4, cy + s*0.25);
      ctx.quadraticCurveTo(cx - s*0.3, cy - s*0.2, cx, cy - s*0.28);
      ctx.quadraticCurveTo(cx + s*0.3, cy - s*0.2, cx + s*0.4, cy + s*0.25);
      ctx.closePath(); ctx.fill();
    },
  },
  {
    key: "treasure",
    label: "Treasure",
    icon: "🏴‍☠️",
    tint: "#fff9db",
    start: { color: "#334155", label: "SHIP" },
    end:   { color: "#f59e0b", label: "TREASURE" },
    drawStart: (ctx, cx, cy, s) => {
      // ship hull
      ctx.fillStyle = "#334155";
      ctx.beginPath();
      ctx.moveTo(cx - s*0.35, cy + s*0.1);
      ctx.lineTo(cx + s*0.35, cy + s*0.1);
      ctx.lineTo(cx + s*0.25, cy + s*0.3);
      ctx.lineTo(cx - s*0.25, cy + s*0.3);
      ctx.closePath(); ctx.fill();
      // mast
      ctx.fillStyle = "#8b4513";
      ctx.fillRect(cx - s*0.02, cy - s*0.35, s*0.04, s*0.45);
      // sail
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.moveTo(cx + s*0.02, cy - s*0.32);
      ctx.lineTo(cx + s*0.25, cy - s*0.05);
      ctx.lineTo(cx + s*0.02, cy - s*0.05);
      ctx.closePath(); ctx.fill();
    },
    drawEnd: (ctx, cx, cy, s) => {
      // chest
      ctx.fillStyle = "#8b4513";
      ctx.fillRect(cx - s*0.32, cy - s*0.1, s*0.64, s*0.4);
      ctx.fillStyle = "#f59e0b";
      ctx.fillRect(cx - s*0.32, cy - s*0.1, s*0.64, s*0.1);
      ctx.fillStyle = "#000";
      ctx.fillRect(cx - s*0.03, cy - s*0.03, s*0.06, s*0.1);
      // star
      ctx.fillStyle = "#fbbf24";
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI/2 + i * 2*Math.PI/5;
        ctx.lineTo(cx + Math.cos(a)*s*0.15, cy - s*0.28 + Math.sin(a)*s*0.15);
        const a2 = a + Math.PI/5;
        ctx.lineTo(cx + Math.cos(a2)*s*0.06, cy - s*0.28 + Math.sin(a2)*s*0.06);
      }
      ctx.closePath(); ctx.fill();
    },
  },
];

function generateMaze(cols, rows) {
  const cells = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({
      top: true, right: true, bottom: true, left: true, v: false,
    }))
  );
  const stack = [[0, 0]];
  cells[0][0].v = true;
  const dirs = [
    [0, -1, "top", "bottom"],
    [1, 0, "right", "left"],
    [0, 1, "bottom", "top"],
    [-1, 0, "left", "right"],
  ];
  while (stack.length) {
    const [x, y] = stack[stack.length - 1];
    const opts = dirs
      .map(([dx, dy, w1, w2]) => [x + dx, y + dy, w1, w2])
      .filter(([nx, ny]) =>
        nx >= 0 && ny >= 0 && nx < cols && ny < rows && !cells[ny][nx].v
      );
    if (!opts.length) {
      stack.pop();
      continue;
    }
    const [nx, ny, w1, w2] = opts[Math.floor(Math.random() * opts.length)];
    cells[y][x][w1] = false;
    cells[ny][nx][w2] = false;
    cells[ny][nx].v = true;
    stack.push([nx, ny]);
  }
  return cells;
}

function drawMaze(canvas, maze, showSolution = false, theme = THEMES[0]) {
  const ctx = canvas.getContext("2d");
  const cols = maze[0].length;
  const rows = maze.length;
  const size = Math.min(
    Math.floor((canvas.width  - 20) / cols),
    Math.floor((canvas.height - 20) / rows)
  );
  const ox = (canvas.width - size * cols) / 2;
  const oy = (canvas.height - size * rows) / 2;
  ctx.fillStyle = theme.tint || "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#0f172a";
  ctx.lineWidth = 2;
  ctx.lineCap = "square";
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const c = maze[y][x];
      const px = ox + x * size;
      const py = oy + y * size;
      ctx.beginPath();
      if (c.top) { ctx.moveTo(px, py); ctx.lineTo(px + size, py); }
      if (c.right) { ctx.moveTo(px + size, py); ctx.lineTo(px + size, py + size); }
      if (c.bottom) { ctx.moveTo(px, py + size); ctx.lineTo(px + size, py + size); }
      if (c.left) { ctx.moveTo(px, py); ctx.lineTo(px, py + size); }
      ctx.stroke();
    }
  }
  // Start / Finish text labels
  ctx.fillStyle = theme.start.color;
  ctx.font = "bold 12px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(theme.start.label, ox + size / 2, oy - 4);
  ctx.fillStyle = theme.end.color;
  ctx.fillText(theme.end.label, ox + (cols - 0.5) * size, oy + rows * size + 14);
  // Themed markers
  theme.drawStart(ctx, ox + size / 2, oy + size / 2, size);
  theme.drawEnd(ctx, ox + (cols - 0.5) * size, oy + (rows - 0.5) * size, size);

  if (showSolution) {
    const path = solveMaze(maze);
    if (path.length) {
      ctx.strokeStyle = "#ef4444";
      ctx.lineWidth = Math.max(2, size * 0.15);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      path.forEach(([x, y], i) => {
        const px = ox + (x + 0.5) * size;
        const py = oy + (y + 0.5) * size;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.stroke();
    }
  }
}

function solveMaze(maze) {
  const rows = maze.length;
  const cols = maze[0].length;
  const target = [cols - 1, rows - 1];
  const visited = Array.from({ length: rows }, () => Array(cols).fill(false));
  const stack = [[0, 0, []]];
  while (stack.length) {
    const [x, y, path] = stack.pop();
    if (visited[y][x]) continue;
    visited[y][x] = true;
    const newPath = [...path, [x, y]];
    if (x === target[0] && y === target[1]) return newPath;
    const c = maze[y][x];
    if (!c.top && y > 0 && !visited[y - 1][x])
      stack.push([x, y - 1, newPath]);
    if (!c.right && x < cols - 1 && !visited[y][x + 1])
      stack.push([x + 1, y, newPath]);
    if (!c.bottom && y < rows - 1 && !visited[y + 1][x])
      stack.push([x, y + 1, newPath]);
    if (!c.left && x > 0 && !visited[y][x - 1])
      stack.push([x - 1, y, newPath]);
  }
  return [];
}

export default function FinderMazesPage() {
  const canvasRef = useRef(null);
  const [size, setSize] = useState("family");
  const [themeKey, setThemeKey] = useState("classic");
  const [maze, setMaze] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [seed, setSeed] = useState(0);

  useEffect(() => {
    document.title = "Mazes — HustleFinder Kids";
  }, []);

  useEffect(() => {
    const spec = SIZES.find((s) => s.key === size) || SIZES[1];
    setMaze(generateMaze(spec.cols, spec.rows));
    setShowSolution(false);
  }, [size, seed]);

  const theme = THEMES.find((t) => t.key === themeKey) || THEMES[0];

  useEffect(() => {
    if (canvasRef.current && maze) {
      drawMaze(canvasRef.current, maze, showSolution, theme);
    }
  }, [maze, showSolution, theme]);

  const download = useCallback(() => {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download = `hustlefinder-maze-${size}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  }, [size]);

  return (
    <div
      data-testid="mazes-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16"
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="no-print flex items-center justify-between mb-4">
          <Link
            to="/finder/kids"
            data-testid="mazes-back-link"
            className="text-xs text-emerald-700 hover:underline"
          >
            ← back to Kid Zone
          </Link>
          <div className="flex gap-2">
            <button
              onClick={download}
              data-testid="mazes-download-btn"
              className="text-xs bg-white border border-slate-300 px-3 py-1.5 rounded-full hover:border-emerald-400"
            >
              Download PNG
            </button>
            <button
              onClick={() => window.print()}
              data-testid="mazes-print-btn"
              className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800"
            >
              Print A4
            </button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-1">Mazes</h1>
        <p className="text-sm text-slate-500 mb-5">
          Follow the path from {theme.start.label} to {theme.end.label}. New maze every tap.
        </p>

        <div
          className="no-print flex gap-2 mb-3 overflow-x-auto"
          data-testid="mazes-theme-picker"
        >
          {THEMES.map((t) => (
            <button
              key={t.key}
              onClick={() => setThemeKey(t.key)}
              data-testid={`mazes-theme-${t.key}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition ${
                themeKey === t.key
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"
              }`}
            >
              <span className="mr-1">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        <div
          className="no-print flex gap-2 mb-5"
          data-testid="mazes-size-picker"
        >
          {SIZES.map((s) => (
            <button
              key={s.key}
              onClick={() => setSize(s.key)}
              data-testid={`mazes-size-${s.key}`}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition ${
                size === s.key
                  ? "bg-emerald-600 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:border-emerald-400"
              }`}
            >
              {s.label}
              <div className="text-[10px] opacity-75">
                {s.cols}×{s.rows}
              </div>
            </button>
          ))}
          <button
            onClick={() => setSeed((s) => s + 1)}
            data-testid="mazes-new-btn"
            className="py-2 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            title="New maze"
          >
            ↻
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
          <canvas
            ref={canvasRef}
            width={800}
            height={800}
            data-testid="mazes-canvas"
            className="w-full h-auto"
          />
        </div>

        <div
          className="no-print flex gap-2 mt-4"
          data-testid="mazes-controls"
        >
          <button
            onClick={() => setShowSolution((s) => !s)}
            data-testid="mazes-solution-btn"
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold ${
              showSolution
                ? "bg-red-500 text-white"
                : "bg-white border border-slate-200 hover:border-red-300"
            }`}
          >
            {showSolution ? "Hide path" : "Show solution"}
          </button>
        </div>
      </div>
    </div>
  );
}
